import { call } from '@advanza/api'
import Cookies from 'js-cookie'
import { promosSchema } from '../schemes/promosSchema'
import { changeEntity } from './entities'
import { _changeFilter, _fetchItems } from './lists'

export const PROMOS_RECEIVE_LIST = 'PROMOS_RECEIVE_LIST'
export const PROMOS_CHANGE_FILTER = 'PROMOS_CHANGE_FILTER'
export const PROMOS_REQUEST = 'PROMOS_REQUEST'

export function fetchPromos(filterId = 'all', options = {}) {
    // Get country_code from cookie, default to 'NL'
    const countryCode = Cookies.get('_trustoo_office_country') || 'NL'

    // If filterId is an object, merge country_code into it
    // Otherwise, create a filter object with country_code
    const filterOptions =
        typeof filterId === 'object'
            ? { ...filterId, country_code: countryCode }
            : { ...options, country_code: countryCode, filterId: filterId }

    return _fetchItems(filterOptions, {
        url: 'office/promos/get-list',
        schema: promosSchema,
        requestActionName: PROMOS_REQUEST,
        receiveActionName: PROMOS_RECEIVE_LIST,
        reducerKey: 'promos',
    })
}

export function changePromosFilter(filterId, options) {
    return _changeFilter(filterId, options, {
        reducerKey: 'promos',
        fetchFunc: fetchPromos,
        actionType: PROMOS_CHANGE_FILTER,
    })
}

export function addPromo(options, filterId) {
    return function (dispatch) {
        const newId = Math.random().toString(36).substr(2, 5)
        dispatch(
            changeEntity({
                diff: {
                    promo_id: newId,
                    _errors: {},
                    _saveAttempted: false,
                    ...options,
                },
                key: newId,
                filterId,
                name: 'promos',
                store: 'promos',
            }),
        )
    }
}

export function savePromo(promoId) {
    return function (dispatch, getState) {
        const promo = getState().promos.entities.promos[promoId]
        if (!promo) {
            return Promise.reject(new Error('Promo not found'))
        }
        const isNew = typeof promoId === 'string'

        // Clean up the promo data before sending
        const promoData = { ...promo }

        // Remove temporary string ID for new promos
        if (isNew) {
            delete promoData.promo_id
            // Don't send md5_identifier for new promos - it will be auto-generated on backend
            delete promoData.md5_identifier

            // Ensure country_code is set for new promos (from cookie or default to NL)
            if (!promoData.country_code) {
                promoData.country_code = Cookies.get('_trustoo_office_country') || 'NL'
            }
        }

        // Remove internal fields
        delete promoData._saving
        delete promoData._isTouched
        delete promoData._errors
        delete promoData._beforeSave

        // Ensure user_type is included (even if empty string, convert to null)
        // Always include user_type field if it exists in the promo object
        if (promo.hasOwnProperty('user_type')) {
            promoData.user_type = promo.user_type === '' ? null : promo.user_type
        }

        // Include specific_provider_ids if user_type is 'specific'
        if (promo.user_type === 'specific' && promo.hasOwnProperty('specific_provider_ids')) {
            promoData.specific_provider_ids = promo.specific_provider_ids || null
        } else {
            // Clear specific_provider_ids if user_type is not 'specific'
            promoData.specific_provider_ids = null
        }

        // Set saving state
        dispatch(
            changeEntity({
                store: 'promos',
                name: 'promos',
                key: promoId,
                diff: { _saving: true },
            }),
        )

        return call(`office/promos/save/${isNew ? '' : promoId}`, { json: promoData }).then(
            (response) => {
                dispatch(
                    changeEntity({
                        store: 'promos',
                        name: 'promos',
                        key: promoId,
                        diff: { _saving: false },
                    }),
                )
                if (isNew) {
                    dispatch({ type: 'INVALIDATE_PROMOS' })
                }
            },
            (response) => {
                const { error, ...fields } = response || {}
                if (error === 'fields') {
                    const errors = promo._errors || {}
                    Object.keys(fields).forEach((field) => {
                        errors[field] = JSON.stringify(response[field]) || true
                    })
                    dispatch(
                        changeEntity({
                            store: 'promos',
                            name: 'promos',
                            key: promoId,
                            diff: { _errors: errors, _saving: false },
                            dontTouch: true,
                        }),
                    )
                } else {
                    dispatch(
                        changeEntity({
                            store: 'promos',
                            name: 'promos',
                            key: promoId,
                            diff: { _error: error, _saving: false },
                            dontTouch: true,
                        }),
                    )
                }
                return Promise.reject(response)
            },
        )
    }
}

function deletePromo(promoId) {
    return function (dispatch) {
        const isNew = typeof promoId === 'string'
        const removeFromStore = () => {
            dispatch(changeEntity({ store: 'promos', name: 'promos', remove: true, key: promoId }))
        }
        if (isNew) {
            return removeFromStore()
        }
        return call(`office/promos/delete/${promoId}`, { method: 'post' }).then(removeFromStore)
    }
}

export function deletePromosSelection(filterId) {
    return function (dispatch, getState) {
        const selection = getState().promos.filters[filterId].selection
        const promises = selection.map((id) => dispatch(deletePromo(id)))
        return Promise.all(promises).then(() => {
            dispatch(changePromosFilter(filterId, { selection: [] }))
        })
    }
}
